import HomeApi from './HomeApi';

/**
 * Serviço de monitoramento de sessão
 * Verifica periodicamente o status da sessão e notifica quando está próxima de expirar
 */
class SessionMonitor {
  constructor() {
    this.homeApi = new HomeApi();
    this.intervalId = null;
    this.warningCallback = null;
    this.expiredCallback = null;
    this.checkIntervalMs = 60000; // Verifica a cada 1 minuto
    this.warningThresholdMinutes = 5; // Alerta quando faltar 5 minutos
  }

  /**
   * Inicia monitoramento de sessão
   * @param {Function} onWarning - Callback quando sessão está próxima de expirar (recebe minutos restantes)
   * @param {Function} onExpired - Callback quando sessão expirou
   */
  start(onWarning, onExpired) {
    this.warningCallback = onWarning;
    this.expiredCallback = onExpired;

    console.log('🔍 Session Monitor iniciado');

    // Verifica imediatamente
    this.checkSession();

    // Depois verifica periodicamente
    this.intervalId = setInterval(() => {
      this.checkSession();
    }, this.checkIntervalMs);
  }

  /**
   * Para monitoramento de sessão
   */
  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
      console.log('🔍 Session Monitor parado');
    }
  }

  /**
   * Verifica status da sessão no backend
   */
  async checkSession() {
    try {
      const data = await this.homeApi.getExpiraEm();
      const minutosRestantes = data.numero_de_minutos || data;

      console.log(`⏰ Sessão expira em ${minutosRestantes} minuto(s)`);

      // Se sessão já expirou (0 minutos ou negativo)
      if (minutosRestantes <= 0) {
        console.error('🔒 Sessão expirada (0 minutos restantes)');
        if (this.expiredCallback) {
          this.expiredCallback();
        }
        this.stop(); // Para de monitorar

        // Limpa localStorage e força redirecionamento
        localStorage.removeItem('usuario_movimentacoes_servidores');
        import('../router').then((routerModule) => {
          const router = routerModule.default;
          router.replace({ name: 'Autenticar' });
        });
        return;
      }

      if (minutosRestantes <= this.warningThresholdMinutes && minutosRestantes > 0) {
        // Sessão próxima de expirar
        if (this.warningCallback) {
          this.warningCallback(minutosRestantes);
        }
      }
    } catch (error) {
      // Se retornar 401, sessão já expirou
      if (error.response && error.response.status === 401) {
        console.error('🔒 Sessão expirada detectada pelo monitor');
        if (this.expiredCallback) {
          this.expiredCallback();
        }
        this.stop(); // Para de monitorar
      } else if (error.response && error.response.status === 404) {
        // Servidor não encontrado - para de monitorar
        console.warn('⚠️ Servidor não encontrado - parando monitoramento');
        this.stop();
      } else {
        // Outros erros - apenas loga mas continua monitorando
        console.warn('⚠️ Erro ao verificar sessão (será tentado novamente)');
      }
    }
  }

  /**
   * Força renovação de sessão fazendo uma chamada ao backend
   * O backend automaticamente renova os cookies JWT
   */
  async renewSession() {
    try {
      await this.homeApi.getUsuarioAtual();
      console.log('🔄 Sessão renovada com sucesso');
      return true;
    } catch (error) {
      console.error('❌ Erro ao renovar sessão:', error);
      return false;
    }
  }

  /**
   * Configura intervalo de verificação (em milissegundos)
   * @param {number} intervalMs - Intervalo em milissegundos
   */
  setCheckInterval(intervalMs) {
    this.checkIntervalMs = intervalMs;

    // Se já está rodando, reinicia com novo intervalo
    if (this.intervalId) {
      this.stop();
      this.start(this.warningCallback, this.expiredCallback);
    }
  }

  /**
   * Configura limite de aviso (em minutos)
   * @param {number} minutes - Minutos antes da expiração para mostrar aviso
   */
  setWarningThreshold(minutes) {
    this.warningThresholdMinutes = minutes;
  }
}

export default SessionMonitor;
